import { useDisclosure, useLocalStorage } from "@mantine/hooks"
import { CurrencyInr } from "@phosphor-icons/react"
import {
  FormInput,
  FormQuerySelector,
  FormSelector,
  Toast,
} from "@vesatogo/grass-core"
import { cloneDeep, get } from "lodash-es"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import AttachmentModal from "~/components/AttachmentModal"
import { FormCard } from "~/components/FormCard"
import { TradeKinds } from "~/constants/Kinds"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAllCommoditiesQuery,
  useAllPackagingCommoditiesQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
  useAuthenticatedUserQuery,
  useTradeSelectorQuery,
} from "~/generated/graphql"

import toast from "react-hot-toast"
import CollapsibleItem from "./CollapsibleItem"
import DetailTable from "./DetailTable"
import ProductionSection from "./ProductionSection"
import SelectPattiModal from "./SelectPattiModal"

export const ProductionParticulars = ({
  form,
  inputProps,
  runComputations,
  isFinal,
  canEditFinalPatti,
  completelyDisabled,
}: {
  form: any
  inputProps: any
  runComputations: (values: any) => void
  isFinal: boolean
  canEditFinalPatti: boolean
  completelyDisabled?: boolean
}) => {
  const { values, setValues } = form
  const [activeAttachmentPath, setActiveAttachmentPath] = useState<
    string | null
  >(null)
  const [pattiModal, { open: openPattiModal, close: closePattiModal }] =
    useDisclosure(false)
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  const [selectedSection, setSelectedSection] = useState<
    "input_items" | "packaging_items" | null
  >(null)

  const [params, setSearchParams] = useSearchParams()
  const commodityId = params.get("commodity_id")
  const varietyId = params.get("variety_id")
  const disableFinal = isFinal
  const disableFinalEdit = disableFinal && !canEditFinalPatti

  const handleClosePattiModal = () => {
    params.delete("commodity_id")
    params.delete("variety_id")
    setSearchParams(params)
    setSelectedIdx(null)
    setSelectedSection(null)
    closePattiModal()
  }
  const [{ data: aData }] = useAuthenticatedUserQuery()

  const currentDeptCodename = aData?.me?.agent?.current_department?.codename
  const [department] = useLocalStorage({ key: "department" })

  const [{ data: allStps, fetching: stpLoading }] = useTradeSelectorQuery({
    variables: {
      where: {
        kind: {
          _eq: TradeKinds.StoragePatti,
        },
        trade_items: {
          commodity_id: commodityId
            ? {
                _eq: commodityId,
              }
            : undefined,
          commodity_variety_id: varietyId
            ? {
                _eq: varietyId,
              }
            : undefined,
        },
        current_status_id: {
          _eq: "Verified",
        },
        deducted_quantity_remaining: {
          _gt: 0.0,
        },
        department: {
          reference_id: {
            _eq: (department && String(department)) || currentDeptCodename,
          },
        },
      },
    },
    requestPolicy: "network-only",
  })

  const handleSelectStps = (selectedStps: any[]) => {
    if (selectedIdx === null || !selectedSection) return
    const stateCopy = cloneDeep(values)
    const currentItem = stateCopy[selectedSection][selectedIdx]
    if (!currentItem.details) currentItem.details = []

    selectedStps.forEach(stp => {
      currentItem.details.push({
        storage_patti_refs: stp.id,
        grade: {
          ...stp.trade_items?.[0]?.variety_grade,
          shared_id: stp.trade_items?.[0]?.variety_grade?.id,
        },
        available_qty: stp.deducted_quantity_remaining,
        required_qty: 0,
        cost_per_unit: stp.trade_items?.[0]?.rate,
        available_grades: stp.trade_items || [],
      })
    })

    setValues(stateCopy)
    runComputations?.(stateCopy)
    setSelectedIdx(null)
    setSelectedSection(null)
  }

  const addItem = (key: string, defaultObj: any = {}) => {
    const stateCopy = cloneDeep(values)
    stateCopy[key].push(defaultObj)
    setValues(stateCopy)
    runComputations?.(stateCopy)
  }

  const removeItem = (key: string, index: number) => {
    const stateCopy = cloneDeep(values)
    stateCopy[key].splice(index, 1)
    setValues(stateCopy)
    runComputations?.(stateCopy)
  }

  const addDetail = (key: string, itemIndex: number, defaultObj: any = {}) => {
    const stateCopy = cloneDeep(values)
    if (!stateCopy[key][itemIndex].details) {
      stateCopy[key][itemIndex].details = []
    }
    stateCopy[key][itemIndex].details.push(defaultObj)
    setValues(stateCopy)
    runComputations?.(stateCopy)
  }

  const removeDetail = (
    key: string,
    itemIndex: number,
    detailIndex: number
  ) => {
    const stateCopy = cloneDeep(values)
    stateCopy[key][itemIndex].details.splice(detailIndex, 1)
    setValues(stateCopy)
    runComputations?.(stateCopy)
  }

  return (
    <div className="space-y-6">
      <FormCard title={"Material/Particulars"}>
        {/* Output Section */}
        <ProductionSection
          title="Output"
          colorScheme="green"
          commodityLabel="Commodity"
          onAddCommodity={() => addItem("output_items", { details: [{}] })}
          stats={values.output_items.reduce(
            (acc: any, item: any) => {
              const details = item.details || []
              acc.items += details.length
              acc.qty += details.reduce(
                (sum: number, d: any) => sum + Number(d.qty || 0),
                0
              )
              acc.cost += details.reduce(
                (sum: number, d: any) =>
                  sum + Number(d.qty * d.cost_per_unit || 0),
                0
              )
              return acc
            },
            { items: 0, qty: 0, cost: 0 }
          )}
          disableAdd={disableFinal || completelyDisabled}
        >
          {values.output_items.map((item: any, idx: number) => (
            <CollapsibleItem
              key={idx}
              index={idx}
              disableDelete={disableFinal || completelyDisabled}
              disableAttachment={completelyDisabled}
              onRemove={() => removeItem("output_items", idx)}
              detailTable={
                <DetailTable
                  headers={[
                    {
                      label: "Grade",
                      field: "grade",
                      required: true,
                      render: (dIdx: number) => (
                        <FormQuerySelector
                          placeholder="Select"
                          dataHook={useAllVarietyGradesQuery}
                          variables={{
                            variety: form.values.output_items[idx].variety?.id,
                          }}
                          {...inputProps(
                            `output_items.${idx}.details.${dIdx}.grade`,
                            {
                              group: SharedEntityGroup.KiteGrade,
                            }
                          )}
                          className={"!w-full"}
                          disabled={
                            disableFinalEdit ||
                            inputProps(
                              `output_items.${idx}.details.${dIdx}.grade`,
                              {
                                group: SharedEntityGroup.KiteGrade,
                              }
                            ).disabled
                          }
                        />
                      ),
                    },
                    {
                      label: "Final Qty",
                      field: "qty",
                      required: true,
                      showTotal: true,
                      render: (dIdx: number) => (
                        <FormInput
                          placeholder="Enter"
                          rightElement={
                            <span className="text-brown-300 pr-4 text-sm">
                              Unit
                            </span>
                          }
                          {...inputProps(
                            `output_items.${idx}.details.${dIdx}.qty`
                          )}
                          className={"!w-full"}
                          disabled={
                            disableFinalEdit ||
                            inputProps(
                              `output_items.${idx}.details.${dIdx}.qty`
                            ).disabled
                          }
                        />
                      ),
                    },
                    {
                      label: "Total Cost",
                      field: "total_cost",
                      showTotal: true,
                      render: (dIdx: number) => (
                        <FormInput
                          placeholder="0"
                          leftElement={<CurrencyInr />}
                          value={
                            ((form.values.output_items[idx].details[dIdx].qty *
                              form.values.output_items[idx].details[dIdx]
                                .cost_per_unit) as any) || ""
                          }
                          disabled
                          className={"!w-full"}
                        />
                      ),
                    },
                    {
                      label: "Cost Per Unit",
                      field: "cost_per_unit",
                      required: true,
                      render: (dIdx: number) => (
                        <FormInput
                          placeholder="Enter"
                          rightElement={
                            <span className="text-brown-300 pr-4 text-sm">
                              Unit
                            </span>
                          }
                          {...inputProps(
                            `output_items.${idx}.details.${dIdx}.cost_per_unit`
                          )}
                          className={"!w-full"}
                          disabled={
                            disableFinalEdit ||
                            inputProps(
                              `output_items.${idx}.details.${dIdx}.cost_per_unit`
                            ).disabled
                          }
                        />
                      ),
                    },
                  ]}
                  rows={
                    item.details?.map((d: any) => ({
                      ...d,
                      total_cost:
                        Number(d.qty || 0) * Number(d.cost_per_unit || 0),
                    })) || []
                  }
                  addRowLabel="Grade"
                  onAddRow={() => addDetail("output_items", idx, {})}
                  onRemoveRow={(dIdx: number) =>
                    removeDetail("output_items", idx, dIdx)
                  }
                  onAttachRow={(dIdx: number) =>
                    setActiveAttachmentPath(
                      `output_items.${idx}.details.${dIdx}.attachments`
                    )
                  }
                  namePrefix={`output_items.${idx}.details`}
                  inputProps={inputProps}
                  className="grid-cols-4"
                  addDisable={disableFinalEdit}
                  deleteDisable={disableFinalEdit}
                  showTotals
                />
              }
            >
              <div className="grid grid-cols-2 gap-4 flex-1 items-center">
                <FormQuerySelector
                  label="Commodity"
                  placeholder="Select"
                  required
                  dataHook={useAllCommoditiesQuery}
                  serverSideQuery
                  {...inputProps(`output_items.${idx}.commodity`, {
                    group: SharedEntityGroup.KiteCommodity,
                  })}
                  disabled={
                    disableFinal ||
                    inputProps(`output_items.${idx}.commodity`, {
                      group: SharedEntityGroup.KiteCommodity,
                    }).disabled
                  }
                />
                <div className="flex gap-4 items-center">
                  <FormQuerySelector
                    label="Variety"
                    placeholder="Select"
                    required
                    dataHook={useAllVarietiesQuery}
                    variables={{
                      commodity_id: form.values.output_items[idx].commodity?.id,
                    }}
                    className={"w-full"}
                    {...inputProps(`output_items.${idx}.variety`, {
                      group: SharedEntityGroup.KiteVariety,
                    })}
                    disabled={
                      disableFinal ||
                      inputProps(`output_items.${idx}.variety`, {
                        group: SharedEntityGroup.KiteVariety,
                      }).disabled
                    }
                  />
                </div>
              </div>
            </CollapsibleItem>
          ))}
        </ProductionSection>

        {/* Input Section */}
        <ProductionSection
          title="Input"
          colorScheme="blue"
          commodityLabel="Commodity"
          onAddCommodity={() => addItem("input_items", {})}
          disableAdd={disableFinal || completelyDisabled}
          stats={values.input_items.reduce(
            (acc: any, item: any) => {
              const details = item.details || []
              acc.items += details.length
              acc.qty += details.reduce(
                (sum: number, d: any) => sum + Number(d.required_qty || 0),
                0
              )
              acc.cost += details.reduce(
                (sum: number, d: any) =>
                  sum + Number(d.required_qty * d.cost_per_unit || 0),
                0
              )
              return acc
            },
            { items: 0, qty: 0, cost: 0 }
          )}
        >
          {values.input_items.map((item: any, idx: number) => (
            <CollapsibleItem
              key={idx}
              index={idx}
              onRemove={() => removeItem("input_items", idx)}
              detailTable={
                <DetailTable
                  headers={
                    [
                      {
                        label: "Storage Patti Refs",
                        field: "storage_patti_refs",
                        required: true,
                        totalColSpan: 3,
                        render: (dIdx: number) => (
                          <FormInput
                            placeholder="Select"
                            {...inputProps(
                              `input_items.${idx}.details.${dIdx}.storage_patti_refs`
                            )}
                            className={"!w-full"}
                            disabled
                          />
                        ),
                      },
                      {
                        label: "Grade",
                        field: "grade",
                        required: true,
                        hideInTotal: true,
                        render: (dIdx: number) => {
                          const row = values.input_items[idx].details[dIdx]
                          const options =
                            row.available_grades?.map((item: any) => ({
                              name: item.variety_grade?.name,
                              id: item.variety_grade?.id,
                            })) || []

                          return (
                            <FormSelector
                              placeholder="Select"
                              options={options}
                              {...inputProps(
                                `input_items.${idx}.details.${dIdx}.grade`
                              )}
                              onChange={(val: any) => {
                                const detailPath = `input_items.${idx}.details.${dIdx}`
                                form.setFieldValue(`${detailPath}.grade`, val)
                                form.setFieldValue(
                                  `${detailPath}.grade.shared_id`,
                                  val?.id
                                )

                                const matchedItem = row.available_grades?.find(
                                  (item: any) =>
                                    item.variety_grade?.id === val?.id
                                )
                                if (matchedItem) {
                                  form.setFieldValue(
                                    `${detailPath}.available_qty`,
                                    matchedItem.quantity
                                  )
                                  form.setFieldValue(
                                    `${detailPath}.cost_per_unit`,
                                    matchedItem.rate
                                  )
                                }
                              }}
                              className={"!w-full"}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `input_items.${idx}.details.${dIdx}.grade`,
                                  {
                                    group: SharedEntityGroup.KiteGrade,
                                  }
                                ).disabled
                              }
                            />
                          )
                        },
                      },
                      {
                        label: "Available Qty",
                        field: "available_qty",
                        hideInTotal: true,
                        render: (dIdx: number) => (
                          <FormInput
                            placeholder="Auto"
                            rightElement={
                              <span className="text-brown-300 pr-4 text-sm">
                                Unit
                              </span>
                            }
                            {...inputProps(
                              `input_items.${idx}.details.${dIdx}.available_qty`
                            )}
                            disabled
                          />
                        ),
                      },
                      {
                        label: "Required Qty",
                        field: "required_qty",
                        required: true,
                        showTotal: true,
                        render: (dIdx: number) => {
                          const parentQty =
                            values.input_items[idx].details[dIdx]
                              .parent_trade_required_qty || 0
                          const maxQuantity =
                            parentQty +
                            values.input_items[idx].details[dIdx].available_qty

                          return (
                            <div className="flex flex-col">
                              <FormInput
                                placeholder="Enter"
                                rightElement={
                                  <span className="text-brown-300 pr-4 text-sm">
                                    Unit
                                  </span>
                                }
                                {...inputProps(
                                  `input_items.${idx}.details.${dIdx}.required_qty`
                                )}
                                disabled={
                                  disableFinalEdit ||
                                  inputProps(
                                    `input_items.${idx}.details.${dIdx}.required_qty`,
                                    {
                                      group: SharedEntityGroup.KiteGrade,
                                    }
                                  ).disabled
                                }
                                inputProps={{
                                  onChange: e => {
                                    let value = Number(e.target.value)
                                    let original = Number(maxQuantity)
                                    let result =
                                      value > original
                                        ? original
                                        : e.target.value
                                    inputProps(
                                      `input_items.${idx}.details.${dIdx}.required_qty`
                                    ).onChange(result)
                                  },
                                }}
                              />
                            </div>
                          )
                        },
                      },
                      isFinal && {
                        label: "Weight Loss  Qty",
                        field: "weight_loss_qty",
                        showTotal: true,
                        render: (dIdx: number) => (
                          <div className="flex flex-col">
                            <FormInput
                              placeholder="Enter"
                              rightElement={
                                <span className="text-brown-300 pr-4 text-sm">
                                  kg
                                </span>
                              }
                              {...inputProps(
                                `input_items.${idx}.details.${dIdx}.weight_loss_qty`
                              )}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `input_items.${idx}.details.${dIdx}.weight_loss_qty`
                                ).disabled
                              }
                            />
                          </div>
                        ),
                      },
                      isFinal && {
                        label: "Wastage  Qty",
                        field: "wastage_qty",
                        showTotal: true,
                        render: (dIdx: number) => (
                          <div className="flex flex-col">
                            <FormInput
                              placeholder="Enter"
                              rightElement={
                                <span className="text-brown-300 pr-4 text-sm">
                                  kg
                                </span>
                              }
                              {...inputProps(
                                `input_items.${idx}.details.${dIdx}.wastage_qty`
                              )}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `input_items.${idx}.details.${dIdx}.wastage_qty`
                                ).disabled
                              }
                            />
                          </div>
                        ),
                      },
                      isFinal && {
                        label: "Used Qty",
                        field: "used_qty",
                        required: true,
                        showTotal: true,
                        render: (dIdx: number) => (
                          <div className="flex flex-col">
                            <FormInput
                              placeholder="Enter"
                              rightElement={
                                <span className="text-brown-300 pr-4 text-sm">
                                  kg
                                </span>
                              }
                              {...inputProps(
                                `input_items.${idx}.details.${dIdx}.used_qty`
                              )}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `input_items.${idx}.details.${dIdx}.used_qty`,
                                  {
                                    group: SharedEntityGroup.KiteGrade,
                                  }
                                ).disabled
                              }
                            />
                          </div>
                        ),
                      },
                      {
                        label: "Total Cost",
                        field: "total_cost",
                        showTotal: true,
                        render: (dIdx: number) => (
                          <FormInput
                            placeholder="0"
                            leftElement={<CurrencyInr />}
                            value={
                              ((form.values.input_items[idx].details[dIdx]
                                .required_qty *
                                form.values.input_items[idx].details[dIdx]
                                  .cost_per_unit) as any) || ""
                            }
                            disabled
                          />
                        ),
                      },
                      {
                        label: "Cost Per Unit",
                        field: "cost_per_unit",
                        placeholder: "Enter",
                        colSpan: 2,
                        props: {
                          disabled: true,
                          rightElement: (
                            <span className="text-brown-300 pr-4 text-sm">
                              Unit
                            </span>
                          ),
                        },
                      },
                    ].filter(a => a) as any
                  }
                  rows={
                    item.details?.map((d: any) => ({
                      ...d,
                      total_cost:
                        Number(d.required_qty || 0) *
                        Number(d.cost_per_unit || 0),
                    })) || []
                  }
                  addRowLabel="STP"
                  onAddRow={() => {
                    if (
                      !values.input_items[idx]?.commodity?.shared_id ||
                      !values.input_items[idx]?.variety?.shared_id
                    ) {
                      return toast.custom(
                        <Toast
                          title="Please select commodity and variety"
                          intent="danger"
                        />
                      )
                    }
                    params.set(
                      "commodity_id",
                      values.input_items[idx].commodity.shared_id
                    )
                    params.set(
                      "variety_id",
                      values.input_items[idx].variety.shared_id
                    )
                    setSearchParams(params)
                    setSelectedIdx(idx)
                    setSelectedSection("input_items")
                    openPattiModal()
                  }}
                  onRemoveRow={(dIdx: number) =>
                    removeDetail("input_items", idx, dIdx)
                  }
                  onAttachRow={(dIdx: number) =>
                    setActiveAttachmentPath(
                      `input_items.${idx}.details.${dIdx}.attachments`
                    )
                  }
                  namePrefix={`input_items.${idx}.details`}
                  inputProps={inputProps}
                  className={isFinal ? "grid-cols-10" : "grid-cols-7"}
                  addDisable={disableFinalEdit || completelyDisabled}
                  deleteDisable={disableFinalEdit || completelyDisabled}
                  showTotals
                />
              }
              disableDelete={disableFinal || completelyDisabled}
              disableAttachment={completelyDisabled}
            >
              <div className="grid grid-cols-2 gap-4 flex-1 items-center">
                <FormSelector
                  label="Commodity"
                  placeholder="Select"
                  required
                  options={values.output_items
                    .map((item: any) => ({
                      ...item?.commodity,
                    }))
                    ?.filter((item: any) => item?.id)}
                  {...inputProps(`input_items.${idx}.commodity`, {
                    group: SharedEntityGroup.KiteCommodity,
                  })}
                  disabled={
                    disableFinal ||
                    inputProps(`input_items.${idx}.commodity`, {
                      group: SharedEntityGroup.KiteCommodity,
                    }).disabled
                  }
                />
                <div className="flex gap-4 items-center">
                  <FormQuerySelector
                    label="Variety"
                    placeholder="Select"
                    required
                    dataHook={useAllVarietiesQuery}
                    variables={{
                      commodity_id: form.values.input_items[idx].commodity?.id,
                    }}
                    className={"w-full"}
                    {...inputProps(`input_items.${idx}.variety`, {
                      group: SharedEntityGroup.KiteVariety,
                    })}
                    disabled={
                      disableFinal ||
                      inputProps(`input_items.${idx}.variety`, {
                        group: SharedEntityGroup.KiteVariety,
                      }).disabled
                    }
                  />
                </div>
              </div>
            </CollapsibleItem>
          ))}
        </ProductionSection>

        {/* Packaging material Section */}

        <ProductionSection
          title="Packaging Material"
          colorScheme="blue"
          commodityLabel="Commodity"
          onAddCommodity={() => addItem("packaging_items", {})}
          disableAdd={disableFinal || completelyDisabled}
          stats={values.packaging_items.reduce(
            (acc: any, item: any) => {
              const details = item.details || []
              acc.items += details.length
              acc.qty += details.reduce(
                (sum: number, d: any) => sum + Number(d.required_qty || 0),
                0
              )
              acc.cost += details.reduce(
                (sum: number, d: any) =>
                  sum + Number(d.required_qty * d.cost_per_unit || 0),
                0
              )
              return acc
            },
            { items: 0, qty: 0, cost: 0 }
          )}
        >
          {values.packaging_items.map((item: any, idx: number) => (
            <CollapsibleItem
              key={idx}
              index={idx}
              onRemove={() => removeItem("packaging_items", idx)}
              detailTable={
                <DetailTable
                  headers={
                    [
                      {
                        label: "Storage Patti Refs",
                        field: "storage_patti_refs",
                        required: true,
                        totalColSpan: 3,
                        render: (dIdx: number) => (
                          <FormInput
                            placeholder="Select"
                            {...inputProps(
                              `packaging_items.${idx}.details.${dIdx}.storage_patti_refs`
                            )}
                            className={"!w-full"}
                            disabled
                          />
                        ),
                      },
                      {
                        label: "Grade",
                        field: "grade",
                        required: true,
                        hideInTotal: true,
                        render: (dIdx: number) => {
                          const row = values.packaging_items[idx].details[dIdx]
                          const options =
                            row.available_grades?.map((item: any) => ({
                              name: item.variety_grade?.name,
                              id: item.variety_grade?.id,
                            })) || []

                          return (
                            <FormSelector
                              placeholder="Select"
                              options={options}
                              {...inputProps(
                                `packaging_items.${idx}.details.${dIdx}.grade`
                              )}
                              onChange={(val: any) => {
                                const detailPath = `packaging_items.${idx}.details.${dIdx}`
                                form.setFieldValue(`${detailPath}.grade`, val)
                                form.setFieldValue(
                                  `${detailPath}.grade.shared_id`,
                                  val?.id
                                )

                                const matchedItem = row.available_grades?.find(
                                  (item: any) =>
                                    item.variety_grade?.id === val?.id
                                )
                                if (matchedItem) {
                                  form.setFieldValue(
                                    `${detailPath}.available_qty`,
                                    matchedItem.quantity
                                  )
                                  form.setFieldValue(
                                    `${detailPath}.cost_per_unit`,
                                    matchedItem.rate
                                  )
                                }
                              }}
                              className={"!w-full"}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `packaging_items.${idx}.details.${dIdx}.grade`,
                                  {
                                    group: SharedEntityGroup.KiteGrade,
                                  }
                                ).disabled
                              }
                            />
                          )
                        },
                      },
                      {
                        label: "Available Qty",
                        field: "available_qty",
                        hideInTotal: true,
                        render: (dIdx: number) => (
                          <FormInput
                            placeholder="Auto"
                            rightElement={
                              <span className="text-brown-300 pr-4 text-sm">
                                Unit
                              </span>
                            }
                            {...inputProps(
                              `packaging_items.${idx}.details.${dIdx}.available_qty`
                            )}
                            disabled
                          />
                        ),
                      },
                      {
                        label: "Required Qty",
                        field: "required_qty",
                        required: true,
                        showTotal: true,
                        render: (dIdx: number) => {
                          const parentQty =
                            values.packaging_items[idx].details[dIdx]
                              .parent_trade_required_qty || 0
                          const maxQuantity =
                            parentQty +
                            values.packaging_items[idx].details[dIdx]
                              .available_qty

                          return (
                            <div className="flex flex-col">
                              <FormInput
                                placeholder="Enter"
                                rightElement={
                                  <span className="text-brown-300 pr-4 text-sm">
                                    Unit
                                  </span>
                                }
                                {...inputProps(
                                  `packaging_items.${idx}.details.${dIdx}.required_qty`
                                )}
                                disabled={
                                  disableFinalEdit ||
                                  inputProps(
                                    `packaging_items.${idx}.details.${dIdx}.required_qty`,
                                    {
                                      group: SharedEntityGroup.KiteGrade,
                                    }
                                  ).disabled
                                }
                                inputProps={{
                                  onChange: e => {
                                    let value = Number(e.target.value)
                                    let original = Number(maxQuantity)
                                    let result =
                                      value > original
                                        ? original
                                        : e.target.value
                                    inputProps(
                                      `packaging_items.${idx}.details.${dIdx}.required_qty`
                                    ).onChange(result)
                                  },
                                }}
                              />
                            </div>
                          )
                        },
                      },
                      isFinal && {
                        label: "Weight Loss  Qty",
                        field: "weight_loss_qty",
                        showTotal: true,
                        render: (dIdx: number) => (
                          <div className="flex flex-col">
                            <FormInput
                              placeholder="Enter"
                              rightElement={
                                <span className="text-brown-300 pr-4 text-sm">
                                  kg
                                </span>
                              }
                              {...inputProps(
                                `packaging_items.${idx}.details.${dIdx}.weight_loss_qty`
                              )}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `packaging_items.${idx}.details.${dIdx}.weight_loss_qty`
                                ).disabled
                              }
                            />
                          </div>
                        ),
                      },
                      isFinal && {
                        label: "Wastage  Qty",
                        field: "wastage_qty",
                        showTotal: true,
                        render: (dIdx: number) => (
                          <div className="flex flex-col">
                            <FormInput
                              placeholder="Enter"
                              rightElement={
                                <span className="text-brown-300 pr-4 text-sm">
                                  kg
                                </span>
                              }
                              {...inputProps(
                                `packaging_items.${idx}.details.${dIdx}.wastage_qty`
                              )}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `packaging_items.${idx}.details.${dIdx}.wastage_qty`
                                ).disabled
                              }
                            />
                          </div>
                        ),
                      },
                      isFinal && {
                        label: "Used Qty",
                        field: "used_qty",
                        required: true,
                        showTotal: true,
                        render: (dIdx: number) => (
                          <div className="flex flex-col">
                            <FormInput
                              placeholder="Enter"
                              rightElement={
                                <span className="text-brown-300 pr-4 text-sm">
                                  kg
                                </span>
                              }
                              {...inputProps(
                                `packaging_items.${idx}.details.${dIdx}.used_qty`
                              )}
                              disabled={
                                disableFinalEdit ||
                                inputProps(
                                  `packaging_items.${idx}.details.${dIdx}.used_qty`,
                                  {
                                    group: SharedEntityGroup.KiteGrade,
                                  }
                                ).disabled
                              }
                            />
                          </div>
                        ),
                      },
                      {
                        label: "Total Cost",
                        field: "total_cost",
                        showTotal: true,
                        render: (dIdx: number) => (
                          <FormInput
                            placeholder="0"
                            leftElement={<CurrencyInr />}
                            value={
                              ((form.values.packaging_items[idx].details[dIdx]
                                .required_qty *
                                form.values.packaging_items[idx].details[dIdx]
                                  .cost_per_unit) as any) || ""
                            }
                            disabled
                          />
                        ),
                      },
                      {
                        label: "Cost Per Unit",
                        field: "cost_per_unit",
                        placeholder: "Enter",
                        colSpan: 2,
                        props: {
                          disabled: true,
                          rightElement: (
                            <span className="text-brown-300 pr-4 text-sm">
                              Unit
                            </span>
                          ),
                        },
                      },
                    ].filter(a => a) as any
                  }
                  rows={
                    item.details?.map((d: any) => ({
                      ...d,
                      total_cost:
                        Number(d.required_qty || 0) *
                        Number(d.cost_per_unit || 0),
                    })) || []
                  }
                  addRowLabel="STP"
                  onAddRow={() => {
                    if (
                      !values.packaging_items[idx]?.commodity?.shared_id ||
                      !values.packaging_items[idx]?.variety?.shared_id
                    ) {
                      return toast.custom(
                        <Toast
                          title="Please select commodity and variety"
                          intent="danger"
                        />
                      )
                    }
                    params.set(
                      "commodity_id",
                      values.packaging_items[idx].commodity.shared_id
                    )
                    params.set(
                      "variety_id",
                      values.packaging_items[idx].variety.shared_id
                    )
                    setSearchParams(params)
                    setSelectedIdx(idx)
                    setSelectedSection("packaging_items")
                    openPattiModal()
                  }}
                  onRemoveRow={(dIdx: number) =>
                    removeDetail("packaging_items", idx, dIdx)
                  }
                  onAttachRow={(dIdx: number) =>
                    setActiveAttachmentPath(
                      `packaging_items.${idx}.details.${dIdx}.attachments`
                    )
                  }
                  namePrefix={`packaging_items.${idx}.details`}
                  inputProps={inputProps}
                  className={isFinal ? "grid-cols-10" : "grid-cols-7"}
                  addDisable={disableFinalEdit || completelyDisabled}
                  deleteDisable={disableFinalEdit || completelyDisabled}
                  showTotals
                />
              }
              disableDelete={disableFinal || completelyDisabled}
              disableAttachment={completelyDisabled}
            >
              <div className="grid grid-cols-2 gap-4 flex-1 items-center">
                <FormQuerySelector
                  label="Commodity"
                  placeholder="Select"
                  required
                  dataHook={useAllPackagingCommoditiesQuery}
                  // options={values.output_items
                  //   .map((item: any) => ({
                  //     ...item?.commodity,
                  //   }))
                  //   ?.filter((item: any) => item?.id)}
                  // {...inputProps(`packaging_items.${idx}.commodity`, {
                  //   group: SharedEntityGroup.KiteCommodity,
                  // })}
                  serverSideQuery
                  {...inputProps(`packaging_items.${idx}.commodity`, {
                    group: SharedEntityGroup.KiteCommodity,
                  })}
                  disabled={
                    disableFinal ||
                    inputProps(`packaging_items.${idx}.commodity`, {
                      group: SharedEntityGroup.KiteCommodity,
                    }).disabled
                  }
                />
                <div className="flex gap-4 items-center">
                  <FormQuerySelector
                    label="Variety"
                    placeholder="Select"
                    required
                    dataHook={useAllVarietiesQuery}
                    variables={{
                      commodity_id:
                        form.values.packaging_items[idx].commodity?.id,
                    }}
                    className={"w-full"}
                    {...inputProps(`packaging_items.${idx}.variety`, {
                      group: SharedEntityGroup.KiteVariety,
                    })}
                    disabled={
                      disableFinal ||
                      inputProps(`packaging_items.${idx}.variety`, {
                        group: SharedEntityGroup.KiteVariety,
                      }).disabled
                    }
                  />
                </div>
              </div>
            </CollapsibleItem>
          ))}
        </ProductionSection>

        {/* <ProductionSection
          title="Packaging Material"
          colorScheme="orange"
          commodityLabel="Packaging Material"
          onAddCommodity={() => addItem("packaging_items", {})}
          disableAdd={disableFinal || completelyDisabled}
        >
          {values.packaging_items.map((item: any, idx: number) => (
            <CollapsibleItem
              key={idx}
              index={idx}
              disableDelete={disableFinal || completelyDisabled}
              disableAttachment={completelyDisabled}
              onRemove={() => removeItem("packaging_items", idx)}
              onAttach={() =>
                setActiveAttachmentPath(`packaging_items.${idx}.attachments`)
              }
              attachmentCount={item.attachments?.length || 0}
            >
              <div className="grid grid-cols-2 gap-4 flex-1 items-center">
                <div className="flex gap-4 items-center">
                  <FormQuerySelector
                    label="Packaging Material"
                    placeholder="Select"
                    required
                    dataHook={useAllPackingMaterialsSelectorQuery}
                    className={"w-full"}
                    {...inputProps(
                      `packaging_items.${idx}.packaging_material`,
                      {
                        group: SharedEntityGroup.StarlingPackaging,
                      }
                    )}
                    disabled={
                      disableFinal ||
                      inputProps(`packaging_items.${idx}.packaging_material`, {
                        group: SharedEntityGroup.StarlingPackaging,
                      }).disabled
                    }
                  />
                </div>
              </div>
            </CollapsibleItem>
          ))}
        </ProductionSection> */}
      </FormCard>

      <SelectPattiModal
        isOpen={pattiModal}
        onClose={handleClosePattiModal}
        onSelect={handleSelectStps}
        data={allStps?.trade || []}
        commodityName={
          selectedIdx !== null && selectedSection
            ? values[selectedSection][selectedIdx]?.commodity?.name
            : ""
        }
        varietyName={
          selectedIdx !== null && selectedSection
            ? values[selectedSection][selectedIdx]?.variety?.name
            : ""
        }
        isLoadingData={stpLoading}
      />
      {activeAttachmentPath && (
        <AttachmentModal
          isOpen={!!activeAttachmentPath}
          onClose={() => setActiveAttachmentPath(null)}
          attachments={get(values, activeAttachmentPath) || []}
          addAttachment={attachments => {
            form.setFieldValue(activeAttachmentPath, attachments)
          }}
          form={form}
        />
      )}
    </div>
  )
}
